import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle, Loader2, LogOut } from "lucide-react";
import { apiDelete, apiGet, apiPut } from "../../utils/api";
import { getRole } from "../../utils/authService";
import { useTeamFilter } from "../../utils/useTeamFilter";
import { displayName } from "../../utils/displayName";
import { FilterBar, PillSelect, DataGrid } from "../common/ui.jsx";
import "./screenshot-settings.css";

const MANAGE_ROLES = ["admin", "client", "ceo", "cfo"];
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";

const EMPTY_S3_FORM = {
  endpoint: "",
  region: "us-east-1",
  bucket: "",
  accessKeyId: "",
  secretAccessKey: "",
  keyPrefix: "",
};

export default function ScreenshotSettings() {
  const role = useMemo(() => getRole(), []);
  const canManage = MANAGE_ROLES.includes(role);

  const [config, setConfig] = useState({
    enabled: false,
    provider: "google_drive",
    captureIntervalSec: 3,
    retentionDays: 30,
    scopeMode: "all",
    scopedAgentIds: [],
    googleOAuth: { connected: false, email: "" },
    s3: { connected: false, ...EMPTY_S3_FORM, secretAccessKeyMasked: "" },
  });
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [disconnecting, setDisc]    = useState(false);
  const [error, setError]           = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [s3Form, setS3Form]         = useState(EMPTY_S3_FORM);
  const [s3Saving, setS3Saving]     = useState(false);
  const [s3Disconnecting, setS3Disc] = useState(false);
  const [activeTab, setActiveTab]   = useState("google_drive");

  // Per-team/employee capture scope. Mirrors the Team + Employee filter/select
  // pattern used on the Devices page: useTeamFilter for the Team dropdown,
  // server-side pagination via the same /api/agents?page=… endpoint Devices
  // uses (a tenant can have hundreds of employees — fetching and rendering
  // them all in one shot doesn't scale), and DataGrid's built-in "select all"
  // header checkbox, which selects every row on the current page. Selections
  // are kept in a Set independent of the currently-fetched page, so they
  // survive paging back and forth.
  const SCOPE_PAGE_SIZE = 25;
  const { departments, getTeamName } = useTeamFilter();
  const [scopeAgents, setScopeAgents] = useState([]);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [scopeSearch, setScopeSearch] = useState("");
  const [debouncedScopeSearch, setDebouncedScopeSearch] = useState("");
  const [scopeTeam, setScopeTeam]     = useState("all");
  const [scopePage, setScopePage]     = useState(1);
  const [scopeTotalPages, setScopeTotalPages] = useState(1);
  const [scopeTotal, setScopeTotal]   = useState(0);
  const [pendingScopedIds, setPendingScopedIds] = useState(new Set());
  const [scopeDirty, setScopeDirty]   = useState(false);

  // Debounce typed search, landing back on page 1 once it actually changes.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedScopeSearch(scopeSearch);
      setScopePage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [scopeSearch]);

  const handleScopeTeamChange = (val) => {
    setScopeTeam(val);
    setScopePage(1);
  };

  // Only fetches while the picker is actually visible (scopeMode "selected"),
  // and only ever pulls one page at a time.
  useEffect(() => {
    if (config.scopeMode !== "selected") return;
    let active = true;
    setScopeLoading(true);
    const params = new URLSearchParams({ page: String(scopePage), limit: String(SCOPE_PAGE_SIZE), nameOnly: "1" });
    if (debouncedScopeSearch.trim()) params.set("search", debouncedScopeSearch.trim());
    if (scopeTeam !== "all") params.set("team", scopeTeam);
    apiGet(`/api/agents?${params.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        if (!active) return;
        if (Array.isArray(data?.agents)) {
          setScopeAgents(data.agents);
          setScopeTotalPages(data.totalPages || 1);
          setScopeTotal(data.total || 0);
        }
      })
      .catch(() => {})
      .finally(() => { if (active) setScopeLoading(false); });
    return () => { active = false; };
  }, [config.scopeMode, scopePage, debouncedScopeSearch, scopeTeam]);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await apiGet("/api/screenshot-config");
      const data = await res.json().catch(() => null);
      if (data?.success) {
        setConfig(data);
        setActiveTab(data.provider || "google_drive");
        setPendingScopedIds(new Set(data.scopedAgentIds || []));
        setScopeDirty(false);
        setS3Form((f) => ({
          ...f,
          endpoint: data.s3?.endpoint || "",
          region: data.s3?.region || "us-east-1",
          bucket: data.s3?.bucket || "",
          accessKeyId: data.s3?.accessKeyId || "",
          keyPrefix: data.s3?.keyPrefix || "",
          secretAccessKey: "",
        }));
      }
    } catch {
      setError("Could not load screenshot settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  // After Google OAuth redirect, the URL may contain ?connected=1 or ?error=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "1") {
      setSuccessMsg("Google account connected successfully.");
      // Strip the query param without reloading
      const clean = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", clean);
    } else if (params.get("error")) {
      const codes = {
        oauth_cancelled:    "Google sign-in was cancelled.",
        no_refresh_token:   "No refresh token received — please reconnect and allow offline access.",
        callback_failed:    "Failed to complete Google sign-in. Check server logs.",
      };
      setError(codes[params.get("error")] || `OAuth error: ${params.get("error")}`);
      const clean = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", clean);
    }
    void fetchConfig();
  }, [fetchConfig]);

  const save = useCallback(async (overrides = {}) => {
    if (saving) return;
    setSaving(true);
    setError("");
    setSuccessMsg("");
    try {
      const next = { ...config, ...overrides };
      const res = await apiPut("/api/screenshot-config", {
        enabled: next.enabled,
        provider: next.provider,
        captureIntervalSec: next.captureIntervalSec,
        retentionDays: next.retentionDays,
        scopeMode: next.scopeMode,
        scopedAgentIds: next.scopedAgentIds,
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        setError(data?.message || "Could not save settings.");
        return;
      }
      setConfig((c) => ({ ...c, ...next }));
      if (overrides.scopedAgentIds) setScopeDirty(false);
      if (overrides.provider && overrides.provider !== config.provider) {
        const label = overrides.provider === "s3" ? "Object Storage (S3)" : "Google Drive";
        setSuccessMsg(`Switched active storage to ${label}. Connected agents will be updated shortly.`);
      }
    } catch {
      setError("Could not reach the server. Try again.");
    } finally {
      setSaving(false);
    }
  }, [config, saving]);

  const connectGoogle = useCallback(async () => {
    setError("");
    try {
      const res = await apiGet("/api/screenshot-config/google/connect");
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success && data.url) {
        window.location.href = data.url;
      } else {
        setError(data?.message || "Failed to initiate Google connection.");
      }
    } catch {
      setError("Could not reach the server. Try again.");
    }
  }, []);

  const disconnectGoogle = useCallback(async () => {
    if (!window.confirm("Disconnect Google account? Screenshot capture will be disabled.")) return;
    setDisc(true);
    setError("");
    setSuccessMsg("");
    try {
      const res = await apiDelete("/api/screenshot-config/google");
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        setError(data?.message || "Failed to disconnect Google account.");
        return;
      }
      setConfig((c) => ({
        ...c,
        enabled: false,
        googleOAuth: { connected: false, email: "" },
      }));
      setSuccessMsg("Google account disconnected.");
    } catch {
      setError("Could not reach the server. Try again.");
    } finally {
      setDisc(false);
    }
  }, []);

  const saveS3 = useCallback(async () => {
    if (s3Saving) return;
    setS3Saving(true);
    setError("");
    setSuccessMsg("");
    try {
      const res = await apiPut("/api/screenshot-config/s3", s3Form);
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        setError(data?.message || "Could not verify or save storage settings.");
        return;
      }
      setS3Form((f) => ({ ...f, secretAccessKey: "" }));
      setSuccessMsg("Object storage connected and verified.");
      await fetchConfig();
    } catch {
      setError("Could not reach the server. Try again.");
    } finally {
      setS3Saving(false);
    }
  }, [s3Form, s3Saving, fetchConfig]);

  // Flips which already-connected provider is actually used for uploads.
  // Connecting/testing a provider does NOT make it active by itself once a
  // second provider exists — this is the only thing that does.
  const switchProvider = useCallback((provider) => {
    if (provider === config.provider || saving) return;
    save({ provider });
  }, [config.provider, saving, save]);

  const setScopeAll = useCallback(() => {
    if (config.scopeMode === "all" || saving) return;
    save({ scopeMode: "all" });
  }, [config.scopeMode, saving, save]);

  const setScopeSelected = useCallback(() => {
    if (config.scopeMode === "selected" || saving) return;
    save({ scopeMode: "selected" });
  }, [config.scopeMode, saving, save]);

  const saveScopeSelection = useCallback(() => {
    save({ scopeMode: "selected", scopedAgentIds: Array.from(pendingScopedIds) });
  }, [save, pendingScopedIds]);

  const scopeColumns = useMemo(() => [
    {
      key: "name",
      label: "Employee",
      width: 190,
      render: (a) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: "0.76rem" }}>{displayName(a)}</div>
          <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{a.hostname || a.agentId}</div>
        </div>
      ),
    },
    {
      key: "team",
      label: "Team",
      width: 110,
      render: (a) => <span style={{ fontSize: "0.76rem" }}>{getTeamName(a.agentId)}</span>,
    },
  ], [getTeamName]);

  const disconnectS3Storage = useCallback(async () => {
    if (!window.confirm("Disconnect object storage? Screenshot capture will be disabled.")) return;
    setS3Disc(true);
    setError("");
    setSuccessMsg("");
    try {
      const res = await apiDelete("/api/screenshot-config/s3");
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        setError(data?.message || "Failed to disconnect object storage.");
        return;
      }
      setConfig((c) => ({ ...c, enabled: false, s3: { ...c.s3, connected: false } }));
      setSuccessMsg("Object storage disconnected.");
    } catch {
      setError("Could not reach the server. Try again.");
    } finally {
      setS3Disc(false);
    }
  }, []);

  if (loading) {
    return <div className="ss-page"><Loader2 className="ss-spin" size={22} /></div>;
  }

  const { googleOAuth, s3 } = config;
  const activeConnected = config.provider === "s3" ? s3.connected : googleOAuth.connected;

  return (
    <div className="ss-page">
      <p className="ss-subtitle">
        Screenshots are captured on each agent and uploaded directly to the storage you connect
        below — Google Drive, or your own S3-compatible object storage (Contabo, Wasabi,
        Backblaze B2, MinIO, and others). Visun's servers never receive or store the image data.
      </p>

      {error      && <div className="ss-banner ss-banner-error">{error}</div>}
      {successMsg && <div className="ss-banner ss-banner-success">{successMsg}</div>}

      {/* Provider switcher + capture controls — one shared toolbar, common
          to both providers, so it never shifts as you browse tabs below. */}
      <div className="ss-toolbar">
        <div className="ss-tabs">
          <button
            className={`ss-tab ${activeTab === "google_drive" ? "ss-tab-active" : ""}`}
            onClick={() => setActiveTab("google_drive")}
          >
            Google Drive
            {config.provider === "google_drive" && googleOAuth.connected && (
              <span className="ss-active-badge">Active</span>
            )}
          </button>
          <button
            className={`ss-tab ${activeTab === "s3" ? "ss-tab-active" : ""}`}
            onClick={() => setActiveTab("s3")}
          >
            Object Storage (S3)
            {config.provider === "s3" && s3.connected && (
              <span className="ss-active-badge">Active</span>
            )}
          </button>
        </div>

        {activeConnected && (
          <div className="ss-capture-inline">
            <label className="ss-switch">
              <input
                type="checkbox"
                checked={config.enabled}
                disabled={!canManage || saving}
                onChange={(e) => save({ enabled: e.target.checked })}
              />
              <span className="ss-switch-track"><span className="ss-switch-handle" /></span>
            </label>
            <span className="ss-capture-label">Capture</span>

            <span className="ss-capture-divider" />

            <span className="ss-capture-label">Every</span>
            <input
              type="number"
              className="ss-interval-input"
              min={3}
              max={3600}
              value={config.captureIntervalSec}
              disabled={!canManage || saving}
              onChange={(e) => setConfig((c) => ({ ...c, captureIntervalSec: Number(e.target.value) }))}
              onBlur={(e) => save({ captureIntervalSec: Math.max(3, Math.min(3600, Number(e.target.value) || 3)) })}
            />
            <span className="ss-capture-label">sec</span>

            {saving && <Loader2 className="ss-spin-inline" size={13} />}
          </div>
        )}
      </div>

      {activeConnected && (
        <p className="ss-hint ss-key-layout-hint">
          {config.provider === "s3"
            ? <>Object key layout: <code>{s3.keyPrefix ? `${s3.keyPrefix}/` : ""}{"visunai/<agentId>/<YYYY-MM-DD>/screenshots/<timestamp>.jpg"}</code></>
            : <>Drive folder structure: <code>Visun Screenshots / {"<agentId>"} / {"<YYYY-MM-DD>"} / {"<timestamp>"}.jpg</code></>}
        </p>
      )}

      {/* Capture scope — which employees actually get captured while the
          switch above is on. Same Team filter + select-all pattern as the
          Devices page. */}
      {activeConnected && (
        <div className="ss-card">
          <h3 className="ss-card-title">Capture scope</h3>
          <div className="ss-tabs" style={{ marginBottom: "0.75rem" }}>
            <button
              className={`ss-tab ${config.scopeMode === "all" ? "ss-tab-active" : ""}`}
              onClick={setScopeAll}
              disabled={!canManage || saving}
            >
              All employees
            </button>
            <button
              className={`ss-tab ${config.scopeMode === "selected" ? "ss-tab-active" : ""}`}
              onClick={setScopeSelected}
              disabled={!canManage || saving}
            >
              Selected employees
              {config.scopeMode === "selected" && config.scopedAgentIds?.length > 0 && (
                <span className="ss-active-badge">{config.scopedAgentIds.length}</span>
              )}
            </button>
          </div>

          {config.scopeMode === "all" ? (
            <p className="ss-hint">Screenshots are captured for every employee in this tenant.</p>
          ) : (
            <>
              <p className="ss-hint">
                Only the employees checked below will have their screens captured. Use the Team
                filter or search to narrow the list, then use the header checkbox to select
                everyone on the current page — selections are kept as you page through, so you
                can build up a list across multiple pages.
              </p>
              <FilterBar
                search={scopeSearch}
                onSearchChange={setScopeSearch}
                searchPlaceholder="Search by name or hostname…"
              >
                {departments.length > 0 && (
                  <PillSelect value={scopeTeam} onChange={(e) => handleScopeTeamChange(e.target.value)}>
                    <option value="all">All Teams</option>
                    {departments.map((dept) => (
                      <option key={dept._id} value={dept.name}>{dept.name}</option>
                    ))}
                  </PillSelect>
                )}
              </FilterBar>
              <div className="ss-scope-grid">
                <DataGrid
                  columns={scopeColumns}
                  rows={scopeAgents}
                  getRowId={(a) => a.agentId}
                  selectable={canManage}
                  selectedIds={pendingScopedIds}
                  onSelectionChange={(next) => { setPendingScopedIds(next); setScopeDirty(true); }}
                  emptyState={<p>{scopeLoading ? "Loading…" : "No employees match the current search/filter."}</p>}
                />
              </div>
              {scopeTotal > 0 && (
                <div className="ss-scope-pagination">
                  <span className="ss-hint" style={{ margin: 0 }}>
                    {(scopePage - 1) * SCOPE_PAGE_SIZE + 1}–{Math.min(scopePage * SCOPE_PAGE_SIZE, scopeTotal)} of {scopeTotal} employee{scopeTotal !== 1 ? "s" : ""}
                  </span>
                  <div className="ss-scope-pagination-controls">
                    <button
                      className="gc-icon-btn"
                      onClick={() => setScopePage((p) => Math.max(1, p - 1))}
                      disabled={scopePage <= 1 || scopeLoading}
                    >
                      ‹
                    </button>
                    <span className="ss-scope-pagination-page">{scopePage} / {scopeTotalPages}</span>
                    <button
                      className="gc-icon-btn"
                      onClick={() => setScopePage((p) => Math.min(scopeTotalPages, p + 1))}
                      disabled={scopePage >= scopeTotalPages || scopeLoading}
                    >
                      ›
                    </button>
                  </div>
                </div>
              )}
              {canManage && (
                <div className="ss-row" style={{ border: "none", paddingTop: "0.75rem" }}>
                  <span className="ss-hint" style={{ margin: 0 }}>
                    {pendingScopedIds.size} employee{pendingScopedIds.size !== 1 ? "s" : ""} selected
                  </span>
                  <button
                    className="ss-btn ss-btn-primary"
                    onClick={saveScopeSelection}
                    disabled={saving || !scopeDirty}
                  >
                    {saving ? <Loader2 className="ss-spin-inline" size={14} /> : null}
                    {" "}Save Selection
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Google Account Connection */}
      {activeTab === "google_drive" && (
        <div className="ss-card">
          <h3 className="ss-card-title">Google Drive connection</h3>

          {googleOAuth.connected ? (
            <div className="ss-google-connected">
              <CheckCircle size={18} className="ss-check" />
              <span>
                Connected as <strong>{googleOAuth.email}</strong>
                {" "}— screenshots go to <em>Visun Screenshots/</em> in that Drive.
              </span>
              {canManage && config.provider !== "google_drive" && (
                <button
                  className="ss-btn ss-btn-primary"
                  onClick={() => switchProvider("google_drive")}
                  disabled={saving}
                >
                  {saving ? <Loader2 className="ss-spin-inline" size={14} /> : null}
                  {" "}Switch to Google Drive
                </button>
              )}
              {canManage && (
                <button
                  className="ss-btn ss-btn-danger"
                  onClick={disconnectGoogle}
                  disabled={disconnecting}
                >
                  {disconnecting ? <Loader2 className="ss-spin-inline" size={14} /> : <LogOut size={14} />}
                  {" "}Disconnect
                </button>
              )}
            </div>
          ) : (
            <div className="ss-google-disconnected">
              <p className="ss-hint">
                Connect a Google account once. Visun will auto-create a{" "}
                <strong>Visun Screenshots</strong> folder in that Drive and configure
                all agents automatically — no manual setup per agent.
              </p>
              {canManage && (
                <button className="ss-btn ss-btn-primary" onClick={connectGoogle}>
                  Connect Google Account
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* S3-compatible object storage connection */}
      {activeTab === "s3" && (
        <div className="ss-card">
          <h3 className="ss-card-title">Object storage connection</h3>
          <p className="ss-hint">
            Works with any S3-compatible provider — Contabo Object Storage, Wasabi, Backblaze B2,
            MinIO, DigitalOcean Spaces, or AWS S3. Enter your bucket's endpoint and credentials.
          </p>

          {s3.connected && (
            <div className="ss-google-connected ss-connected-block">
              <CheckCircle size={18} className="ss-check" />
              <span>
                Connected to <strong>{s3.bucket}</strong> at <code>{s3.endpoint}</code>
              </span>
              {canManage && config.provider !== "s3" && (
                <button
                  className="ss-btn ss-btn-primary"
                  onClick={() => switchProvider("s3")}
                  disabled={saving}
                >
                  {saving ? <Loader2 className="ss-spin-inline" size={14} /> : null}
                  {" "}Switch to Object Storage
                </button>
              )}
              {canManage && (
                <button
                  className="ss-btn ss-btn-danger"
                  onClick={disconnectS3Storage}
                  disabled={s3Disconnecting}
                >
                  {s3Disconnecting ? <Loader2 className="ss-spin-inline" size={14} /> : <LogOut size={14} />}
                  {" "}Disconnect
                </button>
              )}
            </div>
          )}

          {canManage && (
            <>
              <label className="ss-row">
                <span>Endpoint URL</span>
                <input
                  className="ss-input"
                  type="text"
                  placeholder="https://usc1.contabostorage.com"
                  value={s3Form.endpoint}
                  disabled={s3Saving}
                  onChange={(e) => setS3Form((f) => ({ ...f, endpoint: e.target.value }))}
                />
              </label>
              <label className="ss-row">
                <span>Region</span>
                <input
                  className="ss-input"
                  type="text"
                  placeholder="us-east-1"
                  value={s3Form.region}
                  disabled={s3Saving}
                  onChange={(e) => setS3Form((f) => ({ ...f, region: e.target.value }))}
                />
              </label>
              <label className="ss-row">
                <span>Bucket</span>
                <input
                  className="ss-input"
                  type="text"
                  placeholder="my-screenshots-bucket"
                  value={s3Form.bucket}
                  disabled={s3Saving}
                  onChange={(e) => setS3Form((f) => ({ ...f, bucket: e.target.value }))}
                />
              </label>
              <label className="ss-row">
                <span>Access Key ID</span>
                <input
                  className="ss-input"
                  type="text"
                  value={s3Form.accessKeyId}
                  disabled={s3Saving}
                  onChange={(e) => setS3Form((f) => ({ ...f, accessKeyId: e.target.value }))}
                />
              </label>
              <label className="ss-row">
                <span>Secret Access Key</span>
                <input
                  className="ss-input"
                  type="password"
                  placeholder={s3.secretAccessKeyMasked ? `Saved (${s3.secretAccessKeyMasked}) — leave blank to keep` : ""}
                  value={s3Form.secretAccessKey}
                  disabled={s3Saving}
                  onChange={(e) => setS3Form((f) => ({ ...f, secretAccessKey: e.target.value }))}
                />
              </label>
              <label className="ss-row">
                <span>Key prefix (optional)</span>
                <input
                  className="ss-input"
                  type="text"
                  placeholder="screenshots"
                  value={s3Form.keyPrefix}
                  disabled={s3Saving}
                  onChange={(e) => setS3Form((f) => ({ ...f, keyPrefix: e.target.value }))}
                />
              </label>

              <p className="ss-hint">
                Object key layout: <code>{"<prefix>/visunai/<agentId>/<YYYY-MM-DD>/screenshots/<timestamp>.jpg"}</code>.
                Credentials are encrypted at rest and verified with a test upload before saving.
              </p>

              <button className="ss-btn ss-btn-primary" onClick={saveS3} disabled={s3Saving}>
                {s3Saving ? <Loader2 className="ss-spin-inline" size={14} /> : null}
                {" "}{s3Saving ? "Verifying & saving…" : "Test & Save"}
              </button>
            </>
          )}
        </div>
      )}

    </div>
  );
}
